/**
 * @class NavigationDrawer
 * @classdesc Handles the functionality of the navigation drawer
 *
 * @extends Core.Component
 */
export default class NavigationDrawer extends Core.Component {
  constructor(el, context) {
    super(el, context);

    this.headerWrapper = this.el.closest('.section-header');
    this.parentSection = this.el.closest('section');
    this.header = this.headerWrapper.querySelector('[data-header]');
    this.navigation = this.el.querySelector('[data-nav-drawer-navigation]');
    this.tier1Nav = this.navigation.querySelector('[data-navigation-tier-1]');
    this.summaryElements = this.navigation.querySelectorAll('[data-nav-drawer-summary]');

    this.isOpen = false;

    this.summaryAnimations = {};
    this.focusInCloseEvents = {};
    this.focusInStopEvents = {};

    this.openAnimation = Core.Animations.transition({ el: this.el, state: 'closed' });

    this.summaryElements.forEach(summaryElement => this._initSummary(summaryElement));
  }

  onMount() {
    this.siteOverlay = Core.Components.getComponent('site-overlay');
  }

  /**
   * Opens the navigation drawer
   */
  open() {
    if (this.siteOverlay) this.siteOverlay.show();

    const style = getComputedStyle(this.el);
    const announcementHeight = style.getPropertyValue('--announcement-height');
    const headerRect = this.header.getBoundingClientRect();

    if (headerRect.bottom > 0) {
      if (announcementHeight) {
        window.scrollTo({ top: announcementHeight, behavior: 'smooth' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }

    Core.Utils.scrollLock(this.el, this.navigation);

    this.openAnimation.animateTo('open', {
      onStart: () => {
        const { height } = this.el.getBoundingClientRect();
        this.el.style.setProperty('--mobile-nav-height', `${height}px`);
        this.el.dataset.heightSet = true;

        if (this.headerWrapper) {
          this.headerWrapper.dataset.mobileNavOpen = true;
        }
      },
    }).then(() => {
      this.el.style.setProperty('--mobile-nav-height', 'auto');
      this.el.dataset.heightSet = false;
      this.navigation.style.setProperty('--navigation-height', `${this.tier1Nav.scrollHeight}px`);

      const { innerHeight } = window;
      this.el.style.setProperty('--window-height', `${innerHeight}px`);

      this.el.querySelector('[data-nav-drawer-summary], [data-nav-drawer-link]').focus();
    });

    this.addressBarEvent = this.events.register(window, 'resize', () => {
      const { innerHeight } = window;
      this.el.style.setProperty('--window-height', `${innerHeight}px`);
    });
  }

  /**
   * Closes the navigation drawer
   */
  close() {
    Core.Utils.scrollUnlock(this.el);

    if (this.closing) return;

    this.closing = true;

    this.openAnimation.animateTo('closed', {
      onStart: () => {
        const { height } = this.el.getBoundingClientRect();
        this.el.style.setProperty('--mobile-nav-height', `${height}px`);
        this.el.dataset.heightSet = true;

        this.navigation.style.setProperty('--navigation-height', `${this.tier1Nav.scrollHeight}px`);
      },
    }).then(() => {
      this.el.dataset.heightSet = false;

      if (this.headerWrapper) {
        this.headerWrapper.dataset.mobileNavOpen = false;
      }

      this._closeAllSummaries();

      this.closing = false;
      this.events.unregister(this.addressBarEvent);
    });

    if (this.siteOverlay) this.siteOverlay.hide();
  }

  /**
   * Closes all open submenus
   * @private
   */
  _closeAllSummaries() {
    this.summaryElements.forEach(summaryElement => {
      const { parentElement } = summaryElement;
      const { index } = summaryElement.dataset;

      this.summaryAnimations[index].animateTo('closed');

      parentElement.open = false;
      summaryElement.setAttribute('aria-expanded', 'false');

      if (this.focusInCloseEvents[index]) {
        this.events.unregister(this.focusInCloseEvents[index]);
      }

      if (this.focusInStopEvents[index]) {
        this.events.unregister(this.focusInStopEvents[index]);
      }
    });
  }

  /**
   * Triggered when a summary element is clicked and should open the content of the details
   * with another drawer.
   *
   * @param {Summary} summaryElement The summary element that has received a click event
   * @private
   */
  _initSummary(summaryElement) {
    const { index } = summaryElement.dataset;
    const contentElement = summaryElement.nextElementSibling;
    const backButton = contentElement.querySelector('[data-nav-drawer-back-button]');

    this.summaryAnimations[index] = Core.Animations.transition({ el: contentElement, state: 'closed' });

    this.events.register(summaryElement, 'click', () => {
      this._openSummary(summaryElement);
    });

    this.events.register(backButton, 'click', e => {
      e.preventDefault();
      this._closeSummary(summaryElement);
    });
  }

  /**
   * Opens the submenu passed in (summary element)
   * @param {HTMLElement} summaryElement
   * @private
   */
  _openSummary(summaryElement) {
    const { index } = summaryElement.dataset;
    const contentElement = summaryElement.nextElementSibling;
    const parentPane = summaryElement.closest('[data-navigation-pane]');
    const backButton = contentElement.querySelector('[data-nav-drawer-back-button]');

    this.summaryAnimations[index].animateTo('open', {
      onStart: () => {
        const navHeight = contentElement.getBoundingClientRect().height;
        this.navigation.style.setProperty('--navigation-height', `${navHeight}px`);

        // Scroll to top of menu to avoid jump when we focus on the back link after animating.
        this.el.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      },
    }).then(() => {
      summaryElement.setAttribute('aria-expanded', 'true');
      backButton.focus();
    });

    // A11y: Add a focusin event for the parent pane, if focus enters this parent pane, then
    // we want to close the summary that is opening.
    this.focusInCloseEvents[index] = this.events.register(parentPane, 'focusin', e => {
      e.stopPropagation();
      this._closeSummary(summaryElement);
    });

    // A11y: When focusing within this pane, we don't want to close the pane, the focusin
    // event will fire anywhere else in the mobile nav, and will close this summary otherwise.
    this.focusInStopEvents[index] = this.events.register(contentElement, 'focusin', e => e.stopPropagation());
  }

  /**
   * Closes the submenu passed in (summary element
   * @param {HTMLElement} summaryElement
   * @private
   */
  _closeSummary(summaryElement) {
    const { parentElement } = summaryElement;
    const { index } = summaryElement.dataset;
    const parentPane = summaryElement.closest('[data-navigation-pane]');

    this.summaryAnimations[index].animateTo('closed', {
      onStart: () => {
        this.navigation.style.setProperty('--navigation-height', `${parentPane.scrollHeight}px`);
      },
    }).then(() => {
      parentElement.open = false;
      summaryElement.setAttribute('aria-expanded', 'false');

      // A11y: Remove the focusin events for this element
      if (this.focusInCloseEvents[index]) {
        this.events.unregister(this.focusInCloseEvents[index]);
      }

      if (this.focusInStopEvents[index]) {
        this.events.unregister(this.focusInStopEvents[index]);
      }

      summaryElement.focus();
    });
  }
}
